import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  Divider,
} from '@mui/material';
import billService, { Bill } from '../services/billService';

interface PayBillDialogProps {
  open: boolean;
  onClose: () => void;
  /** Dipanggil setelah sukses; menerima respons API (berisi info reaktivasi customer) */
  onSuccess: (result?: any) => void;
  bill: Bill;
}

const PayBillDialog: React.FC<PayBillDialogProps> = ({
  open,
  onClose,
  onSuccess,
  bill,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [notes, setNotes] = useState('');

  const paymentMethods = [
    { value: 'cash', label: 'Tunai' },
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('id-ID');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!paymentMethod) {
      setError('Metode pembayaran harus dipilih');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await billService.markBillAsPaid(bill.id, {
        payment_method: paymentMethod,
        notes: notes,
      });
      onSuccess(result);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to mark bill as paid');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPaymentMethod('');
    setNotes('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Konfirmasi Pembayaran Tagihan</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Bill Information */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Detail Tagihan
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: '200px' }}>
                <Typography variant="body2" color="textSecondary">
                  Nomor Tagihan:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {bill.bill_number}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: '200px' }}>
                <Typography variant="body2" color="textSecondary">
                  Customer:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {bill.customer?.name}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: '200px' }}>
                <Typography variant="body2" color="textSecondary">
                  Periode:
                </Typography>
                <Typography variant="body1">
                  {formatDate(bill.billing_period_start)} - {formatDate(bill.billing_period_end)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: '200px' }}>
                <Typography variant="body2" color="textSecondary">
                  Jatuh Tempo:
                </Typography>
                <Typography variant="body1">
                  {formatDate(bill.due_date)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Payment Details */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Rincian Pembayaran
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Jumlah Tagihan:</Typography>
              <Typography variant="body1">{formatCurrency(bill.amount)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Pajak:</Typography>
              <Typography variant="body1">{formatCurrency(bill.tax_amount)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Diskon:</Typography>
              <Typography variant="body1" color="success.main">
                -{formatCurrency(bill.discount_amount)}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6" fontWeight="bold">
                Total Pembayaran:
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {formatCurrency(bill.total_amount)}
              </Typography>
            </Box>
          </Box>

          {/* Payment Form */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Metode Pembayaran</InputLabel>
              <Select
                value={paymentMethod}
                label="Metode Pembayaran"
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                {paymentMethods.map((method) => (
                  <MenuItem key={method.value} value={method.value}>
                    {method.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Catatan Pembayaran"
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Catatan tambahan tentang pembayaran (opsional)"
            />
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            Setelah dikonfirmasi, status tagihan akan berubah menjadi "Lunas" dan tidak dapat diubah kembali.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Batal</Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
            color="success"
          >
            {loading ? 'Memproses...' : 'Konfirmasi Pembayaran'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PayBillDialog;
